import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, Flame, Users, ChefHat, Lightbulb, Loader2, Timer, Scale, Check } from "lucide-react";
import { api } from "@/lib/api";
import { NUTRITION_KEYS } from "@/lib/recipes";

export default function RecipeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setRecipe(null);
    api.get(`/recipes/${id}`).then((r) => setRecipe(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center">
        <p className="text-sm text-slate-500">Recipe not found.</p>
        <button data-testid="recipe-details-back-list" onClick={() => navigate("/recipes")} className="mt-3 text-sm font-bold text-emerald-700">Back to recipes</button>
      </div>
    );
  }
  if (!recipe) return <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="animate-fade-in pb-6" data-testid="recipe-details-page">
      <div className="relative h-64 bg-slate-100 dark:bg-emerald-800">
        <img src={recipe.image} alt={recipe.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <button
          data-testid="recipe-back-btn"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 dark:bg-emerald-950/90 backdrop-blur flex items-center justify-center text-slate-700 dark:text-slate-100 shadow"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="absolute bottom-4 left-5 right-5">
          <span className="text-[11px] font-bold bg-emerald-500 text-emerald-950 px-2.5 py-1 rounded-full">{recipe.category}</span>
          <h1 data-testid="recipe-details-name" className="mt-2 text-xl font-extrabold text-white font-head leading-snug">{recipe.name}</h1>
        </div>
      </div>

      <div className="px-5 pt-4">
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed" data-testid="recipe-details-intro">{recipe.intro}</p>

        <div className="mt-4 grid grid-cols-4 gap-2">
          <Stat icon={Timer} label="Prep" value={recipe.prep_time} />
          <Stat icon={ChefHat} label="Cook" value={recipe.cook_time} />
          <Stat icon={Clock} label="Total" value={recipe.total_time} />
          <Stat icon={Users} label="Serves" value={recipe.serves} />
        </div>

        {recipe.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {recipe.tags.map((t) => (
              <span key={t} className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-800 px-2 py-0.5 rounded-full">{t}</span>
            ))}
          </div>
        )}

        <Block title="Ingredients">
          <ul className="space-y-2">
            {recipe.ingredients.map((ing, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                {ing}
              </li>
            ))}
          </ul>
        </Block>

        <Block title="Step-by-Step Instructions">
          <ol className="space-y-3">
            {recipe.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </Block>

        <Block title="Nutrition (per serving, approx.)">
          <div className="rounded-2xl overflow-hidden border border-slate-100 dark:border-emerald-800" data-testid="recipe-nutrition">
            {NUTRITION_KEYS.filter((k) => recipe.nutrition?.[k]).map((k, i) => (
              <div key={k} className={`flex items-center justify-between px-4 py-2.5 text-sm ${i % 2 === 0 ? "bg-white dark:bg-emerald-900/50" : "bg-slate-50 dark:bg-emerald-900/20"}`}>
                <span className="flex items-center gap-2 text-slate-600 dark:text-slate-400">{k === "Calories" && <Flame className="w-3.5 h-3.5 text-amber-500" />}{k}</span>
                <span className="font-semibold text-emerald-950 dark:text-emerald-50">{recipe.nutrition[k]}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-400 leading-relaxed">{recipe.note}</p>
        </Block>

        <div className="mt-5 p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Healthy Tips</h3>
          </div>
          <ul className="space-y-1.5">
            {recipe.tips.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-amber-800/80 dark:text-amber-200/70 leading-relaxed">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />{t}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-3 p-4 rounded-2xl bg-[#faf6ea] dark:bg-emerald-900/60 border border-[#eadfb9] dark:border-emerald-800">
          <div className="flex items-center gap-2 mb-1.5">
            <Scale className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
            <h3 className="text-sm font-bold text-emerald-950 dark:text-emerald-50">Portion Guidance</h3>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed" data-testid="recipe-portion">{recipe.portion}</p>
          {recipe.suitable_for?.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {recipe.suitable_for.map((s) => (
                <span key={s} className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-200 bg-emerald-100 dark:bg-emerald-800 px-2 py-0.5 rounded-full">{s}</span>
              ))}
            </div>
          )}
          <p className="mt-2 text-[11px] text-slate-400">General nutrition education only — may be suitable depending on individual needs.</p>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="p-2.5 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800 text-center">
    <Icon className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mx-auto" />
    <p className="text-[10px] text-slate-400 mt-1">{label}</p>
    <p className="text-[11px] font-bold text-emerald-950 dark:text-emerald-50 leading-tight">{value}</p>
  </div>
);

const Block = ({ title, children }) => (
  <div className="mt-6">
    <h3 className="text-base font-bold text-emerald-950 dark:text-emerald-50 font-head mb-3">{title}</h3>
    {children}
  </div>
);
